import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { createInvoice } from "./invoices";

export const ESTIMATE_STATUS = {
  DRAFT: "draft",
  SENT: "sent",
  APPROVED: "approved",
  DECLINED: "declined",
  CONVERTED: "converted",
};

function calculateTotals(lineItems = [], taxRate = 0, discount = 0) {
  const subtotal = lineItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
    0
  );
  const taxAmount = subtotal * ((Number(taxRate) || 0) / 100);
  const total = subtotal + taxAmount - (Number(discount) || 0);
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    total: Math.round(Math.max(total, 0) * 100) / 100,
  };
}

async function getNextEstimateNumber(userId) {
  const counterRef = doc(db, "users", userId, "company", "counters");
  const snap = await getDoc(counterRef);
  const current = snap.exists() ? snap.data().estimateCounter || 0 : 0;
  const next = current + 1;
  await setDoc(counterRef, { estimateCounter: next }, { merge: true });
  return `EST-${String(next).padStart(4, "0")}`;
}

/** Generates a random, unguessable token used in the public client-facing link. */
function generateShareId() {
  return (
    Math.random().toString(36).slice(2) +
    Math.random().toString(36).slice(2) +
    Date.now().toString(36)
  );
}

export async function createEstimate(userId, estimateData) {
  const estimatesRef = collection(db, "users", userId, "estimates");
  const estimateNumber = await getNextEstimateNumber(userId);
  const totals = calculateTotals(estimateData.lineItems, estimateData.taxRate, estimateData.discount);

  const docRef = await addDoc(estimatesRef, {
    ...estimateData,
    ...totals,
    estimateNumber,
    status: estimateData.status || ESTIMATE_STATUS.DRAFT,
    issueDate: estimateData.issueDate || new Date().toISOString().split("T")[0],
    shareId: generateShareId(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateEstimate(userId, estimateId, updates) {
  const ref = doc(db, "users", userId, "estimates", estimateId);
  const payload = { ...updates, updatedAt: serverTimestamp() };
  if (updates.lineItems) {
    Object.assign(payload, calculateTotals(updates.lineItems, updates.taxRate, updates.discount));
  }
  await updateDoc(ref, payload);
}

export async function deleteEstimate(userId, estimateId) {
  await deleteDoc(doc(db, "users", userId, "estimates", estimateId));
}

export async function getEstimates(userId) {
  const q = query(collection(db, "users", userId, "estimates"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getEstimate(userId, estimateId) {
  const snap = await getDoc(doc(db, "users", userId, "estimates", estimateId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Converts an approved estimate into a new draft invoice, carrying over
 * the client and line items, then marks the estimate as converted.
 */
export async function convertEstimateToInvoice(userId, estimate) {
  const invoiceId = await createInvoice(userId, {
    clientId: estimate.clientId,
    lineItems: estimate.lineItems,
    taxRate: estimate.taxRate || 0,
    discount: estimate.discount || 0,
    notes: estimate.notes || "",
    sourceEstimateId: estimate.id,
  });

  await updateDoc(doc(db, "users", userId, "estimates", estimate.id), {
    status: ESTIMATE_STATUS.CONVERTED,
    convertedInvoiceId: invoiceId,
    updatedAt: serverTimestamp(),
  });

  return invoiceId;
}